import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Policy as PolicyIcon } from "@mui/icons-material";

const PrivacyPolicyPage: React.FC = () => {
  const lastUpdated = "January 8, 2026";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundImage: "url('/leaves-removebg-preview.png')",
        backgroundBlendMode: "soft-light",
      }}
    >
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <PolicyIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
            <Typography
              variant="h3"
              component="h1"
              fontWeight={700}
              gutterBottom
            >
              Privacy Policy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last Updated: {lastUpdated}
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Typography variant="body1" paragraph>
            At Sanduuq, we take your privacy seriously. This Privacy Policy
            explains how we collect, use, disclose, and safeguard your
            information when you use our lending circle platform. Please read
            this policy carefully to understand our practices regarding your
            personal data.
          </Typography>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              1. Information We Collect
            </Typography>

            <Typography
              variant="h6"
              fontWeight={500}
              sx={{ mt: 3 }}
              gutterBottom
            >
              Personal Information
            </Typography>
            <Typography variant="body1" paragraph>
              When you create an account and use Sanduuq, we collect:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Full legal name" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Email address" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Phone number" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Date of birth" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Residential address" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Profile photo (optional)" />
              </ListItem>
            </List>

            <Typography
              variant="h6"
              fontWeight={500}
              sx={{ mt: 3 }}
              gutterBottom
            >
              Financial Information
            </Typography>
            <Typography variant="body1" paragraph>
              To facilitate lending circle payments, we collect:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Bank account details (for payouts)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Payment method information (processed securely via Stripe)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Transaction history within the platform" />
              </ListItem>
            </List>

            <Typography
              variant="h6"
              fontWeight={500}
              sx={{ mt: 3 }}
              gutterBottom
            >
              Identity Verification
            </Typography>
            <Typography variant="body1" paragraph>
              For security and compliance purposes, we may collect:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Government-issued ID (driver's license, passport, etc.)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Social Security Number (last 4 digits)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Verification selfies" />
              </ListItem>
            </List>

            <Typography
              variant="h6"
              fontWeight={500}
              sx={{ mt: 3 }}
              gutterBottom
            >
              Usage Information
            </Typography>
            <Typography variant="body1" paragraph>
              We automatically collect:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Device information (type, operating system, browser)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• IP address and location data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• App usage patterns and interactions" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Login timestamps and session data" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              2. How We Use Your Information
            </Typography>
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Data Used</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Account creation & management</TableCell>
                    <TableCell>Name, email, phone, password</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Identity verification</TableCell>
                    <TableCell>ID documents, DOB, SSN</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Payment processing</TableCell>
                    <TableCell>Bank details, payment methods</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Circle participation</TableCell>
                    <TableCell>Profile info, payment history</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Fraud prevention</TableCell>
                    <TableCell>All verification data, usage patterns</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Customer support</TableCell>
                    <TableCell>Contact info, transaction records</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Legal compliance</TableCell>
                    <TableCell>As required by law</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              3. Information Sharing
            </Typography>
            <Typography variant="body1" paragraph>
              We share your information only in the following circumstances:
            </Typography>

            <Typography
              variant="h6"
              fontWeight={500}
              sx={{ mt: 2 }}
              gutterBottom
            >
              With Circle Members
            </Typography>
            <Typography variant="body1" paragraph>
              When you join a lending circle, other members can see your first
              name, last initial, and profile photo. Payment status
              (paid/pending) is visible to circle administrators.
            </Typography>

            <Typography
              variant="h6"
              fontWeight={500}
              sx={{ mt: 2 }}
              gutterBottom
            >
              With Service Providers
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Stripe"
                  secondary="For payment processing and bank account verification"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="AWS"
                  secondary="For secure cloud hosting and data storage"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Identity verification providers"
                  secondary="For KYC compliance"
                />
              </ListItem>
            </List>

            <Typography
              variant="h6"
              fontWeight={500}
              sx={{ mt: 2 }}
              gutterBottom
            >
              For Legal Reasons
            </Typography>
            <Typography variant="body1" paragraph>
              We may disclose information when required by law, court order, or
              government request, or to protect our rights, privacy, safety, or
              property.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              4. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement industry-standard security measures including:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• End-to-end encryption for sensitive data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Secure HTTPS connections" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Regular security audits" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Two-factor authentication options" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• PCI-DSS compliant payment processing via Stripe" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              5. Data Retention
            </Typography>
            <Typography variant="body1" paragraph>
              We retain your data for as long as your account is active or as
              needed to provide services. After account deletion:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Personal data is deleted within 30 days" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Transaction records are retained for 7 years (legal requirement)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Anonymized analytics may be retained indefinitely" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              6. Your Rights
            </Typography>
            <Typography variant="body1" paragraph>
              You have the right to:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Access your personal data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Correct inaccurate information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Request deletion of your data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Export your data in a portable format" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Opt out of marketing communications" />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              To exercise these rights, contact us at privacy@sandooq.com
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              7. Cookies & Tracking
            </Typography>
            <Typography variant="body1" paragraph>
              We use essential cookies for authentication and security. We do
              not use third-party advertising cookies. You can manage cookie
              preferences in your browser settings.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              8. Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Sanduuq is not intended for users under 18 years of age. We do not
              knowingly collect information from minors. If we discover we have
              collected data from a minor, we will delete it immediately.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              9. Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. We will
              notify you of significant changes via email or in-app
              notification. Your continued use of Sanduuq after changes
              constitutes acceptance of the updated policy.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Contact Us
            </Typography>
            <Typography variant="body1">
              For privacy-related questions or concerns:
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Email: privacy@sandooq.com
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
